import { CircularProgress, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { Datasets } from "../../../../../constants/enums"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getDisplayName,
    goToPage,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    HP_FLICKER_STANDARDS_REG,
    HP_MANUFACTURER_REG,
    HP_MAX_DEMAND_CURRENT_REG,
    HP_MAX_DEMAND_POWER_REG,
    HP_MODEL_REF_REG,
    HP_MODEL_REG,
    HP_PHASES_REG,
    HP_TOT_INPUT_RATED_CURRENT_REG,
    HP_TOT_INPUT_RATED_POWER_REG,
    HP_VOLTAGE_REG,
    hpDeviceFormLabelMapping,
} from "../../formSpecs/hpSpec"
import { HpDeviceFormData } from "../../types/providerTypes"

interface IReviewAndSubmitProps {
    context: Context<IDeviceFormContextValue<HpDeviceFormData>>
    isAmendment: boolean
}

const ReviewAndSubmitEbDevice = ({
    context,
    isAmendment,
}: IReviewAndSubmitProps): ReactElement => {
    const {
        formData,
        deviceRef,
        currentIndex,
        setCurrentIndex,
        onSubmit,
        submitLoading,
        submitSuccessful,
        canEditManufacturer,
        attachmentUUID,
        errors: contextErrors,
    } = useContext(context)

    const { manufacturerMappings } = useFilterReferenceMappings()

    const {
        uploadedAttachments,
        error: listAttachmentsError,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
    } = useUploadedDeviceAttachments(
        Datasets.EB,
        deviceRef,
        attachmentUUID,
        isAmendment
    )

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !listAttachmentsError &&
        !!uploadedAttachments

    const showPageEdit = !submitLoading

    const goToAttachmentsPage = (): void =>
        goToPage(2, canEditManufacturer, setCurrentIndex)

    const goToCoreEbDeviceDetailsPage = (): void =>
        goToPage(1, canEditManufacturer, setCurrentIndex)

    const goToCoreEbManufacturerPage = (): void =>
        goToPage(0, canEditManufacturer, setCurrentIndex)

    const onClickBack = (): void => setCurrentIndex(currentIndex - 1)

    const showManufacturerEdit = canEditManufacturer && !submitLoading

    const attachmentMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    if (uploadedAttachmentsAreValid) {
        uploadedAttachments.forEach((summary) => {
            attachmentMappings[summary.fileName] = summary.description
        })
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={deviceRef}
                dataset={Datasets.EB}
            />
            <Stack spacing={GRID_SPACING} paddingTop={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please check through your details prior to submitting"
                    index={currentIndex + 1}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "Manufacturer",
                            mappings: {
                                [hpDeviceFormLabelMapping[HP_MANUFACTURER_REG]]:
                                    formData.manufacturerId
                                        ? getDisplayName(
                                              manufacturerMappings,
                                              Number(formData.manufacturerId)
                                          )
                                        : "Not found",
                            },
                            onEdit: showManufacturerEdit
                                ? goToCoreEbManufacturerPage
                                : undefined,
                            omit: !canEditManufacturer,
                        },

                        {
                            title: "Core Electric Boiler Details",
                            mappings: {
                                [hpDeviceFormLabelMapping[HP_MODEL_REG]]:
                                    formData.deviceModel,

                                [hpDeviceFormLabelMapping[HP_MODEL_REF_REG]]:
                                    formData.deviceModelRef,

                                [hpDeviceFormLabelMapping[HP_PHASES_REG]]:
                                    formData.devicePhases ?? undefined,

                                [hpDeviceFormLabelMapping[HP_VOLTAGE_REG]]:
                                    formData.deviceVoltage ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_TOT_INPUT_RATED_CURRENT_REG
                                ]]:
                                    formData.deviceTotInputRatedCurrent ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_TOT_INPUT_RATED_POWER_REG
                                ]]:
                                    formData.deviceTotInputRatedPower ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_MAX_DEMAND_CURRENT_REG
                                ]]:
                                    formData.deviceMaxDemandCurrent ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_MAX_DEMAND_POWER_REG
                                ]]: formData.deviceMaxDemandPower ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_FLICKER_STANDARDS_REG
                                ]]:
                                    formData.deviceFlickerStandards ??
                                    undefined,
                            },
                            onEdit: showPageEdit
                                ? goToCoreEbDeviceDetailsPage
                                : undefined,
                        },
                        {
                            title: `Files - ${
                                uploadedAttachmentsAreValid
                                    ? uploadedAttachments.length
                                    : "Loading"
                            }`,
                            mappings: attachmentMappings,
                            onEdit: showPageEdit
                                ? goToAttachmentsPage
                                : undefined,
                        },
                    ]}
                />

                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={onSubmit}
                    nextText="Submit"
                    backText="Back"
                    nextDisabled={submitLoading}
                    backDisabled={submitLoading}
                    nextIcon={
                        submitLoading ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmitEbDevice
